import type { ComponentType, JSX, ReactNode } from 'react';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { getValidationErrorKey } from '../../constants/form';
import { LengthValidatorType } from '../../enums/ValidatorType';
import { useLocalization } from '../../providers/LocaleProvider';
import { FieldInfo } from '../Form/FieldInfo';
import FormGroupRow from '../Form/FormGroupRow';
import type { FormFieldValidationRules } from './types';
import { getFormFieldValidators } from './utils/validate-form-field';

export interface FormFieldType<TFieldName> {
  name: TFieldName;
  Component: ComponentType<any>;
  initialValue: string | { [key: string]: string };
  validation: FormFieldValidationRules;
  label?: string | JSX.Element;
  bottomContent?: ReactNode;
  ComponentProps?: any;
  helpText?: string;
}

interface FormFieldProps<TFieldName> extends FormFieldType<TFieldName> {
  isLastField: boolean;
  isDisabledWhileSubmitting: boolean;
  disabled?: boolean;
}

const formFieldStyle = {
  mb: 'sp_0',
};

const FormField = <TFieldName extends string>({
  name,
  Component,
  ComponentProps,
  helpText,
  bottomContent,
  isLastField,
  isDisabledWhileSubmitting,
  disabled,
  ...field
}: FormFieldProps<TFieldName>) => {
  const { t } = useLocalization();
  const methods = useFormContext();

  return (
    <Controller
      name={name}
      control={methods.control}
      rules={{
        required:
          field.validation.required &&
          (field.validation.requiredErrorMessage ??
            t(getValidationErrorKey(name, LengthValidatorType.Length))),
        validate: {
          ...getFormFieldValidators(field.validation.rules, t),
          ...field.validation.functions,
        },
      }}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      defaultValue={field.initialValue}
      render={({ field: fieldProps, fieldState: { error }, formState }) => (
        <FormGroupRow {...(isLastField ? { mb: 'sp_0' } : {})}>
          <Component
            inputRef={fieldProps.ref}
            {...ComponentProps}
            {...fieldProps}
            {...formFieldStyle}
            {...(field.label ? { label: field.label } : {})}
            {...(isDisabledWhileSubmitting ? { disabled: formState.isSubmitting } : {})}
            {...(disabled ? { disabled } : {})}
            aria-invalid={!!error}
            hasError={!!error}
            required={field.validation.required}
            aria-required={field.validation.required}
            {...(error?.message
              ? {
                  errors: [
                    field.validation.rulesErrorMessage
                      ? field.validation.rulesErrorMessage
                      : error.message,
                  ],
                }
              : {})}
            {...(error?.types ? { errorsTypes: error.types } : {})}
            formState={formState}
          />
          {!!helpText && <FieldInfo mt="sp_4" info={helpText} />}
          {!!bottomContent && bottomContent}
        </FormGroupRow>
      )}
    />
  );
};

export default FormField;
